/**
 * Pure request-body builder for POST /api/videos, extracted so the wire can be asserted
 * without standing up the whole upload queue — same pattern as `uploadPolling.ts`.
 *
 * This exists because ONE field decides whether a COPPA gate runs. routes/videos.ts:289
 * wraps the entire under-13 photo/video consent check in `if (matchId)`, so a create body
 * without it uploads match footage of minors past a check that never executes. Keeping the
 * body construction in a pure function means that fact is testable rather than buried in a
 * fetch call inside a React effect.
 *
 * KAN-221: the server no longer has an `if (matchId)` branch: a video with no match is refused.
 */

export interface CreateVideoBodyInput {
  title: string;
  fileName: string;
  contentType: string;
  totalBytes: number;
  /** The match this footage is of. Required: every video names its event (KAN-221). */
  matchId: string;
}

export interface CreateVideoBody {
  title: string;
  fileName: string;
  contentType: string;
  totalBytes: number;
  matchId: string;
}

/** What an upload with no match is told. The API refuses it too (VIDEO_MATCH_REQUIRED). */
export const VIDEO_MATCH_REQUIRED_MESSAGE =
  'Every video must be tagged to its match. Choose the match this footage is of.';

/**
 * Builds the POST /api/videos body.
 *
 * KAN-221 (Taylor 2026-09-27: "No video should be able to be uploaded without an event tagged to
 * it"): there is no unlinked upload any more. A body with no match is never built: this throws, so
 * the upload fails on the client with a clear message instead of being sent to be refused.
 */
export function buildCreateVideoBody(input: CreateVideoBodyInput): CreateVideoBody {
  const matchId = typeof input.matchId === 'string' ? input.matchId.trim() : '';
  if (!matchId) throw new Error(VIDEO_MATCH_REQUIRED_MESSAGE);
  return {
    title: input.title,
    fileName: input.fileName,
    contentType: input.contentType,
    totalBytes: input.totalBytes,
    matchId,
  };
}
